import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function CSS_2DTransforms() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🌀 Advanced CSS: 2D Transforms</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS 2D Transforms allow you to change the position, size, and shape of
        elements without disturbing the normal flow. You can move, rotate, skew,
        and scale elements with smooth effects.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            <code>translate(x, y)</code> moves an element along X and Y axis.
          </li>
          <li>
            <code>rotate(angle)</code> rotates element clockwise or
            counterclockwise.
          </li>
          <li>
            <code>scale(x, y)</code> resizes element width/height.
          </li>
          <li>
            <code>skew(x, y)</code> tilts an element by given angles.
          </li>
          <li>
            <code>matrix()</code> combines multiple transforms in one function.
          </li>
        </ul>
      </div>

      {/* Translate */}
      <div className="tutorial-section">
        <h2 className="section-title">📍 Translate</h2>
        <CodeCopyBlock code={`div { transform: translate(50px, 30px); }`} />
        <div
          style={{
            width: "120px",
            height: "120px",
            background: "#38bdf8",
            margin: "1rem",
            transform: "translate(50px, 30px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            borderRadius: "8px",
          }}
        >
          Moved
        </div>
      </div>

      {/* Rotate */}
      <div className="tutorial-section">
        <h2 className="section-title">🔄 Rotate</h2>
        <CodeCopyBlock code={`div { transform: rotate(45deg); }`} />
        <div
          style={{
            width: "120px",
            height: "120px",
            background: "#f97316",
            margin: "1rem",
            transform: "rotate(45deg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            borderRadius: "8px",
          }}
        >
          Rotated
        </div>
      </div>

      {/* Scale */}
      <div className="tutorial-section">
        <h2 className="section-title">📏 Scale</h2>
        <CodeCopyBlock code={`div { transform: scale(1.5, 0.8); }`} />
        <div
          style={{
            width: "120px",
            height: "120px",
            background: "#10b981",
            margin: "1rem",
            transform: "scale(1.5, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            borderRadius: "8px",
          }}
        >
          Scaled
        </div>
      </div>

      {/* Skew */}
      <div className="tutorial-section">
        <h2 className="section-title">📐 Skew</h2>
        <CodeCopyBlock code={`div { transform: skew(20deg, 10deg); }`} />
        <div
          style={{
            width: "120px",
            height: "120px",
            background: "#8b5cf6",
            margin: "1rem",
            transform: "skew(20deg, 10deg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            borderRadius: "8px",
          }}
        >
          Skewed
        </div>
      </div>

      {/* Matrix */}
      <div className="tutorial-section">
        <h2 className="section-title">🧮 Matrix</h2>
        <p>
          The <code>matrix()</code> function combines translate, scale, skew,
          and rotate in one shorthand:
        </p>
        <CodeCopyBlock
          code={`div { transform: matrix(1, 0.5, -0.5, 1, 30, 20); }`}
        />
        <div
          style={{
            width: "120px",
            height: "120px",
            background: "#ef4444",
            margin: "1rem",
            transform: "matrix(1, 0.5, -0.5, 1, 30, 20)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            borderRadius: "8px",
          }}
        >
          Matrix
        </div>
      </div>

      {/* Best Practices */}
      <div className="tutorial-section">
        <h2 className="section-title">✅ Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            Combine multiple transforms using{" "}
            <code>transform: rotate() scale() translate()</code>.
          </li>
          <li>
            Use <code>transform-origin</code> to set pivot point for rotation
            and scaling.
          </li>
          <li>
            Prefer <code>transform</code> over top/left for smoother animations
            (GPU accelerated).
          </li>
          <li>
            Keep transforms minimal for better readability and performance.
          </li>
        </ul>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef3c7, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Explore <b>CSS 3D Transforms</b> like rotateX, rotateY, perspective.
          </li>
          <li>
            Combine transforms with <b>transitions</b> for smooth effects.
          </li>
          <li>
            Build interactive hover animations using transform + scale/rotate.
          </li>
          <li>
            Use <code>transform-origin</code> to change pivot points.
          </li>
          <li>
            Learn <b>transform-style</b> and <b>perspective</b> for 3D depth.
          </li>
        </ul>
      </div>
    </div>
  );
}
